import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Campaign, CampaignSubscriber, CampaignType } from '@/shared/types/campaigns';
import { noop } from '@/util/events';

import type { VerifySubscribersRequest, VerifySubscribersResponse } from '../api';
import { useColumnMappingSuggestions } from './useColumnMappingSuggestions';
import { normalizeIdentities } from '../components/modals/useMatchFieldsModal';
import { getDebtCampaignVariables } from '../constants';
import {
  setDraftSubscribers,
  clearDraftSubscribers,
  setDraftAudienceStats,
  setCsvLiveInstructionTargets,
  setCsvMappingData,
} from '../redux/actions';
import type { DraftCampaignSubscriber } from '../redux/actions';
import {
  selectDraftSubscribers,
  selectDraftCampaignFileName,
  selectDraftCampaignFileSize,
  selectCsvHeaders,
  selectCsvLiveInstructionTargets,
  selectCsvRows,
  selectCsvMappings,
  selectIsQuickAddSource,
} from '../redux/selectors';
import { ColumnMapping } from '../types/column-mapping';
import {
  computeAudienceStats,
  getExtraRequiredFields,
  hasRequiredUnmatchedFields,
  isOutreachCampaign,
  parseCsvFile,
  parseDateToTimestamp,
  showUnmatchedRequiredToast,
  transformCsvWithMappings,
  validateSubscriberFields,
} from '../utils';
import {
  normalizeSubscriberErrorField,
  resolveSubscriberErrorMessage,
} from '../utils/subscriberErrors';

/**
 * Check if CSV headers exactly match available variable names
 * Returns auto-mappings if all headers match, null otherwise
 */
export const tryAutoMapHeaders = (headers: string[]): Record<string, ColumnMapping> | null => {
  const variableNames = new Set(getDebtCampaignVariables().map((v) => v.name));
  const allHeadersMatch = headers.every((h) => variableNames.has(h));

  if (!allHeadersMatch) {
    return null;
  }

  // Create direct mappings: header -> same variable name
  const mappings: Record<string, ColumnMapping> = {};
  headers.forEach((header) => {
    mappings[header] = { target: header };
  });
  return mappings;
};

export interface ParsedCsvData {
  file: File;
  headers: string[];
  rows: string[][];
}

/**
 * Shape transformed subscribers into the verify payload the orchestrator
 * expects post-PR #1839: debt specs live in slot-indexed, compacted
 * `context.debts[]`, alternates under `context.alternate_*`, and singular
 * `context.debt` is forbidden. Shared by the upload and match-fields-confirm
 * paths so the payload stays in one place.
 */
export const buildVerifySubscribersPayload = (
  subscribers: DraftCampaignSubscriber[],
  campaignId: string | undefined
) => ({
  ...(campaignId && { campaign_id: campaignId }),
  subscribers: subscribers.map((sub) => {
    const context: Record<string, unknown> = {};
    const debtSlots = new Map<number, Record<string, unknown>>();

    const writeDebtField = (slot: number, field: string, value: unknown) => {
      let slotObj = debtSlots.get(slot);
      if (!slotObj) {
        slotObj = {};
        debtSlots.set(slot, slotObj);
      }
      if (field === 'amount') {
        slotObj[field] = parseFloat(String(value)) || 0;
      } else if (field === 'due_date') {
        const timestamp = parseDateToTimestamp(String(value));
        if (timestamp !== null) {
          slotObj[field] = timestamp;
        }
      } else {
        slotObj[field] = value;
      }
    };

    for (const [key, value] of Object.entries(sub)) {
      if (key === 'email' || key === 'phone' || key === 'error' || value === undefined) {
        continue;
      }
      // `live_instructions.*` is a save-time mirror; verify validates canonical
      // context fields only.
      if (key.startsWith('live_instructions.')) {
        continue;
      }
      const slottedMatch = key.match(/^debts?\.(\d+)\.(.+)$/);
      if (slottedMatch) {
        writeDebtField(parseInt(slottedMatch[1], 10), slottedMatch[2], value);
        continue;
      }
      // Legacy plain debt key (`debt.<field>`) → slot 0.
      if (key.startsWith('debt.')) {
        writeDebtField(0, key.slice(5), value);
        continue;
      }
      const altMatch = key.match(/^(alternate_phones|alternate_emails)\.(\d+)$/);
      if (altMatch) {
        const bucket = altMatch[1];
        const idx = parseInt(altMatch[2], 10);
        if (!Array.isArray(context[bucket])) {
          context[bucket] = [];
        }
        (context[bucket] as string[])[idx] = String(value);
        continue;
      }
      context[key] = value;
    }

    // Compact sparse alternate_* arrays so unmapped slots don't leak `undefined`.
    for (const bucket of ['alternate_phones', 'alternate_emails'] as const) {
      const arr = context[bucket];
      if (Array.isArray(arr)) {
        const compact = (arr as unknown[]).filter(
          (v): v is string => typeof v === 'string' && v.length > 0
        );
        if (compact.length > 0) {
          context[bucket] = compact;
        } else {
          delete context[bucket];
        }
      }
    }

    // Dense `context.debts[]` in ascending slot order, dropping empty slots.
    if (debtSlots.size > 0) {
      const orderedSlots = Array.from(debtSlots.keys()).sort((a, b) => a - b);
      const debts = orderedSlots
        .map((slot) => debtSlots.get(slot) ?? {})
        .filter((debt) => Object.keys(debt).length > 0);
      if (debts.length > 0) {
        context.debts = debts;
      }
    }

    return { email: sub.email, phone: sub.phone, context, metadata: {} as Record<string, unknown> };
  }),
});

interface UseAudienceUploadOptions {
  campaignId: string;
  campaign: Campaign | undefined;
  campaignType?: CampaignType;
  apiSubscribers: CampaignSubscriber[] | undefined;
  hasUserDeletedAudience: boolean;
  setHasUserDeletedAudience: (value: boolean) => void;
  setShouldReplaceSubscribers: (value: boolean) => void;
  verifySubscribersAsync: (payload: VerifySubscribersRequest) => Promise<VerifySubscribersResponse>;
  form: { setValue: (field: string, value: unknown) => void };
  onAudienceDelete?: () => void;
}

export const useAudienceUpload = ({
  campaignId,
  campaign,
  campaignType,
  apiSubscribers,
  hasUserDeletedAudience,
  setHasUserDeletedAudience,
  setShouldReplaceSubscribers,
  verifySubscribersAsync,
  form,
  onAudienceDelete,
}: UseAudienceUploadOptions) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { suggestMappingsAsync } = useColumnMappingSuggestions();

  // Redux state
  const subscribers = useSelector(selectDraftSubscribers);
  const draftFileName = useSelector(selectDraftCampaignFileName);
  const draftFileSize = useSelector(selectDraftCampaignFileSize);
  const csvHeaders = useSelector(selectCsvHeaders);
  const csvRows = useSelector(selectCsvRows);
  const csvMappings = useSelector(selectCsvMappings);
  const csvLiveInstructionTargets = useSelector(selectCsvLiveInstructionTargets);

  const isQuickAddSource = useSelector(selectIsQuickAddSource);

  // File upload state - pendingFile provides immediate feedback during upload
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Column mapping modal state
  const [matchFieldsModalOpen, setMatchFieldsModalOpen] = useState(false);
  const [parsedCsvData, setParsedCsvData] = useState<ParsedCsvData | null>(null);
  // Restore savedCsvData from Redux so "match columns" works after navigation
  const [savedCsvData, setSavedCsvData] = useState<ParsedCsvData | null>(() => {
    if (csvHeaders && csvRows) {
      return {
        file: new File([], draftFileName || 'restored.csv'),
        headers: csvHeaders,
        rows: csvRows,
      };
    }
    return null;
  });
  const [savedMappings, setSavedMappings] = useState<Record<string, ColumnMapping> | null>(
    () => csvMappings ?? null
  );
  const [skipAutoSuggest, setSkipAutoSuggest] = useState(false);
  const isConfirmingMappingsRef = useRef(false);
  // Invalidates in-flight continuations (new upload, delete, unmount) so stale results never dispatch.
  const uploadSeqRef = useRef(0);
  const cancelParseRef = useRef<() => void>(noop);
  useEffect(
    () => () => {
      uploadSeqRef.current += 1;
      cancelParseRef.current();
    },
    []
  );

  // Derived state
  const hasExistingSubscribers = !!apiSubscribers && apiSubscribers.length > 0;

  // Compute selected file from sources (avoids effect-based state sync)
  const selectedFile = useMemo(() => {
    // Pending file during upload (immediate feedback)
    if (pendingFile) {
      return pendingFile;
    }

    // File in active modal session
    if (parsedCsvData?.file) {
      return parsedCsvData.file;
    }

    // Draft subscribers in Redux (uploaded and confirmed)
    if (subscribers.length > 0 && draftFileName) {
      const mockFile = new File([''], draftFileName, { type: 'text/csv' });
      if (draftFileSize !== undefined) {
        Object.defineProperty(mockFile, 'size', { value: draftFileSize, writable: false });
      }
      return mockFile;
    }

    // API subscribers (existing campaign data)
    if (!hasUserDeletedAudience && apiSubscribers && apiSubscribers.length > 0 && campaign) {
      const fileName = `${campaign.name} - ${t('campaigns.audience')}.csv`;
      return new File([''], fileName, { type: 'text/csv' });
    }

    return null;
  }, [
    pendingFile,
    parsedCsvData?.file,
    subscribers.length,
    draftFileName,
    draftFileSize,
    hasUserDeletedAudience,
    apiSubscribers,
    campaign,
    t,
  ]);

  /**
   * Server-verify subscribers (Tier A + B) and attach each per-row error to the
   * matching subscriber. On failure, surface an inline file error and return
   * the rows unverified so the flow can still proceed.
   */
  const verifyAndAttachErrors = useCallback(
    async (subs: DraftCampaignSubscriber[]): Promise<DraftCampaignSubscriber[]> => {
      try {
        const errors = await verifySubscribersAsync(
          buildVerifySubscribersPayload(subs, campaignId)
        );
        return subs.map((sub, idx) => {
          const err = Array.isArray(errors) ? errors.find((e) => e.index === idx) : undefined;
          return err
            ? {
                ...sub,
                error: resolveSubscriberErrorMessage(err, t),
                errorField: normalizeSubscriberErrorField(err),
              }
            : sub;
        });
      } catch (error) {
        // Rows save and the flow continues → the dropzone unmounts, so only a toast reaches the user.
        console.error('Verification failed:', error);
        toast.error(t('campaigns.audience_verification_failed'));
        return subs;
      }
    },
    [verifySubscribersAsync, campaignId, t]
  );

  /** Transform → validate → verify → persist; null on zero contact rows or a stale run. */
  const applyMappingsToDraft = useCallback(
    async (
      csvData: ParsedCsvData,
      mappings: Record<string, ColumnMapping>,
      liveInstructionTargets: string[],
      { onZeroRows, isStale }: { onZeroRows: () => void; isStale?: () => boolean }
    ): Promise<DraftCampaignSubscriber[] | null> => {
      const { file, headers, rows } = csvData;
      const raw = transformCsvWithMappings(headers, rows, mappings, liveInstructionTargets);
      // No row resolved to an email or phone → no usable contact column.
      if (raw.length === 0) {
        onZeroRows();
        return null;
      }
      const validated = validateSubscriberFields(raw, t);
      const withErrors = await verifyAndAttachErrors(validated);
      if (isStale?.()) {
        return null;
      }
      dispatch(
        setDraftSubscribers({ subscribers: withErrors, fileName: file.name, fileSize: file.size })
      );
      dispatch(setDraftAudienceStats(computeAudienceStats(withErrors, headers.length)));
      dispatch(setCsvMappingData({ headers, mappings, rows: rows.slice(0, 10000) }));
      dispatch(setCsvLiveInstructionTargets(liveInstructionTargets));
      form.setValue('audience', file);
      form.setValue('fileName', file.name);
      setSavedCsvData(csvData);
      setSavedMappings(mappings);
      return withErrors;
    },
    [dispatch, form, t, verifyAndAttachErrors]
  );

  /** Handle file upload/removal */
  const handleFileSelect = useCallback(
    async (file: File | null) => {
      // Every call (upload or delete) invalidates the previous run's continuations.
      const seq = ++uploadSeqRef.current;
      const isStale = () => seq !== uploadSeqRef.current;
      cancelParseRef.current();
      setFileError('');
      setPendingFile(file);

      if (!file) {
        dispatch(clearDraftSubscribers());
        form.setValue('audience', null);
        form.setValue('fileName', '');
        setHasUserDeletedAudience(true);
        setIsProcessing(false);
        setParsedCsvData(null);
        setSavedCsvData(null);
        setSavedMappings(null);
        setShouldReplaceSubscribers(false);
        onAudienceDelete?.();
        return;
      }

      setHasUserDeletedAudience(false);
      if (hasExistingSubscribers) {
        setShouldReplaceSubscribers(true);
      }

      const onParseError = (error: string) => {
        if (isStale()) {
          return;
        }
        setPendingFile(null);
        setFileError(error);
        setIsProcessing(false);
      };

      const isOutreach = isOutreachCampaign(campaignType);
      setIsProcessing(true);

      if (isOutreach) {
        // Outreach: parse CSV directly (no column mapping modal)
        cancelParseRef.current = parseCsvFile(
          file,
          async (result) => {
            const rawSubscribers = (result.subscribers || []).map((sub) => ({
              ...sub,
              phone: sub.phone && !sub.phone.startsWith('+') ? `+${sub.phone}` : sub.phone,
            }));
            const subscribers = validateSubscriberFields(rawSubscribers, t);

            // Use campaign-scoped verify (Tier A + Tier B) when an existing
            // campaign is being edited; fall back to legacy Tier-A-only verify
            // for fresh/draft campaigns. See Orchestrator PR #1839 §4.
            const verificationPayload = {
              subscribers: subscribers.map((sub) => ({
                email: sub.email,
                phone: sub.phone,
                context: {},
                metadata: {},
              })),
              ...(campaignId && { campaign_id: campaignId }),
            };

            try {
              const errors = await verifySubscribersAsync(verificationPayload);
              if (isStale()) {
                return;
              }
              const subscribersWithErrors = subscribers.map((sub, idx) => {
                const err = Array.isArray(errors) ? errors.find((e) => e.index === idx) : undefined;
                return err
                  ? {
                      ...sub,
                      error: resolveSubscriberErrorMessage(err, t),
                      errorField: normalizeSubscriberErrorField(err),
                    }
                  : sub;
              });

              dispatch(
                setDraftSubscribers({
                  subscribers: subscribersWithErrors,
                  fileName: file.name,
                  fileSize: file.size,
                })
              );
              dispatch(
                setDraftAudienceStats(
                  computeAudienceStats(subscribersWithErrors, result.headers?.length || 0)
                )
              );
            } catch {
              if (isStale()) {
                return;
              }
              dispatch(
                setDraftSubscribers({ subscribers, fileName: file.name, fileSize: file.size })
              );
              dispatch(
                setDraftAudienceStats(
                  computeAudienceStats(subscribers, result.headers?.length || 0)
                )
              );
            }

            form.setValue('audience', file);
            form.setValue('fileName', file.name);
            setPendingFile(null);
            setFileError('');
            setIsProcessing(false);
          },
          onParseError
        );
      } else {
        // Payment recovery: parse CSV, auto-resolve mappings, verify, and fill
        // the audience summary without opening the matcher. The matcher only
        // opens when the user acts on the toast (or the panel link).
        cancelParseRef.current = parseCsvFile(
          file,
          async (result) => {
            const headers = result.headers || [];
            const rows = result.rows || [];
            const csvData = { file, headers, rows };
            setPendingFile(null);
            setFileError('');

            // Resolve mappings WITHOUT opening the matcher: exact-header match,
            // or the LLM auto-suggest.
            const autoMappings = tryAutoMapHeaders(headers);
            let mappings: Record<string, ColumnMapping>;
            if (autoMappings) {
              mappings = autoMappings;
            } else {
              try {
                const response = await suggestMappingsAsync({
                  headers,
                  sample_rows: rows.slice(0, 3),
                  available_variables: getDebtCampaignVariables(),
                });
                mappings = normalizeIdentities(response.mappings, headers);
              } catch (suggestError) {
                // Suggest failed: fall back to the manual matcher (the file may be fine) instead of erroring with a false "no contact" message.
                console.error('Failed to get column mapping suggestions:', suggestError);
                if (isStale()) {
                  return;
                }
                setIsProcessing(false);
                setSavedCsvData(csvData);
                setParsedCsvData(csvData);
                setMatchFieldsModalOpen(true);
                return;
              }
              if (isStale()) {
                return;
              }
            }

            const transformed = await applyMappingsToDraft(csvData, mappings, [], {
              onZeroRows: () => {
                setIsProcessing(false);
                setFileError(t('file_upload.csv.missing_required_columns'));
              },
              isStale,
            });
            // Null → zero usable rows (handled in onZeroRows) or a stale run.
            if (!transformed) {
              return;
            }
            setIsProcessing(false);
            setSkipAutoSuggest(true);

            // Still missing a required field → prompt to match (toast only; the
            // matcher opens on the toast action or the "Match fields" link).
            if (hasRequiredUnmatchedFields(mappings, getExtraRequiredFields(transformed))) {
              // Seed parsedCsvData too — the modal is gated on it in Setup.tsx.
              showUnmatchedRequiredToast(t, () => {
                if (isStale()) {
                  return;
                }
                setParsedCsvData(csvData);
                setMatchFieldsModalOpen(true);
              });
            }
          },
          onParseError,
          { skipRequiredColumnValidation: true }
        );
      }
    },
    [
      campaignId,
      campaignType,
      hasExistingSubscribers,
      dispatch,
      form,
      setHasUserDeletedAudience,
      setShouldReplaceSubscribers,
      onAudienceDelete,
      verifySubscribersAsync,
      applyMappingsToDraft,
      suggestMappingsAsync,
      t,
    ]
  );

  /** Process confirmed column mappings and verify subscribers */
  const handleMappingsConfirm = useCallback(
    async (mappings: Record<string, ColumnMapping>, liveInstructionTargets: string[]) => {
      isConfirmingMappingsRef.current = true;

      if (!parsedCsvData) {
        return;
      }

      // The matcher closes synchronously on confirm, so the dropzone is live while
      // verify awaits — guard the dispatch like the upload paths do, so deleting the
      // audience in that window isn't clobbered by this in-flight confirm.
      const seq = uploadSeqRef.current;
      const isStale = () => seq !== uploadSeqRef.current;

      setFileError('');
      const applied = await applyMappingsToDraft(parsedCsvData, mappings, liveInstructionTargets, {
        // No contact column → zero rows; keep the previous mapping instead of wiping the audience.
        onZeroRows: () => toast.error(t('file_upload.csv.missing_required_columns')),
        isStale,
      });
      if (applied) {
        setParsedCsvData(null);
      }
    },
    [parsedCsvData, applyMappingsToDraft, t]
  );

  /** Add single subscriber via quick add */
  const handleAddSubscriber = useCallback(
    (subscriber: DraftCampaignSubscriber) => {
      if (hasExistingSubscribers) {
        setShouldReplaceSubscribers(true);
      }
      dispatch(
        setDraftSubscribers({
          subscribers: [...subscribers, subscriber],
          fileName: draftFileName || 'quick-add.csv',
        })
      );
      setHasUserDeletedAudience(false);
    },
    [
      subscribers,
      hasExistingSubscribers,
      dispatch,
      draftFileName,
      setHasUserDeletedAudience,
      setShouldReplaceSubscribers,
    ]
  );

  // Resolve the CSV data to feed the modal. Prefer the in-session capture, but
  // fall back to the Redux CSV view — which `useInitializeCampaignRedux`
  // rehydrates from saved subscribers after a page refresh, when `savedCsvData`
  // (a mount-time snapshot) would otherwise be null and the modal couldn't open.
  const resolveCsvDataForReopen = useCallback((): ParsedCsvData | null => {
    if (savedCsvData) {
      return savedCsvData;
    }
    if (csvHeaders && csvRows) {
      return {
        file: new File([], draftFileName || 'audience.csv', { type: 'text/csv' }),
        headers: csvHeaders,
        rows: csvRows,
      };
    }
    return null;
  }, [savedCsvData, csvHeaders, csvRows, draftFileName]);

  /** Open the modal showing the current mappings (skip LLM — review/re-map). */
  const openMatchFieldsForReview = useCallback(() => {
    const data = resolveCsvDataForReopen();
    if (!data) {
      return;
    }
    setParsedCsvData(data);
    // Seed mappings from the rehydrated Redux view when no session mappings
    // exist, so the modal shows the current matches instead of all-unmapped.
    if (!savedMappings && csvMappings) {
      setSavedMappings(csvMappings);
    }
    setSkipAutoSuggest(true);
    setMatchFieldsModalOpen(true);
  }, [resolveCsvDataForReopen, savedMappings, csvMappings]);

  /** Re-open match fields modal with saved mappings (skip LLM) */
  const handleMatchMissingFields = openMatchFieldsForReview;

  /** Handle modal close - clear file and reset state if cancelled */
  const handleModalOpenChange = useCallback(
    (open: boolean) => {
      setMatchFieldsModalOpen(open);
      if (!open) {
        if (!isConfirmingMappingsRef.current) {
          // User cancelled the modal - reset pending file and replace flag
          setPendingFile(null);
          setShouldReplaceSubscribers(false);
        }
        setParsedCsvData(null);
        isConfirmingMappingsRef.current = false;
      }
    },
    [setShouldReplaceSubscribers]
  );

  return {
    // State
    selectedFile,
    fileError,
    isProcessing,
    matchFieldsModalOpen,
    parsedCsvData,
    savedMappings,
    savedLiveInstructionTargets: csvLiveInstructionTargets,
    skipAutoSuggest,
    subscribers,
    draftFileName,
    isQuickAddSource,

    // Handlers
    handleFileSelect,
    handleMappingsConfirm,
    handleAddSubscriber,
    handleMatchMissingFields,
    handleModalOpenChange,
  };
};
